/**
 * What the first run shows, from the record. Fail closed: no record, a
 * template signup, or a landed run shows nothing; a run sent but not landed
 * resumes at the primer on its canvas; a brand-new user gets the question.
 */

import type {
  UserOnboarding,
  UserOnboardingFirstRunBrief,
} from "@cohesive/api-client";
import { useEffect, useSyncExternalStore } from "react";
import { useOnboardingRecord } from "../kernel/status";
import type { Stage } from "../kernel/types";
import { isFirstRunOverridden, useFirstRunEnabled } from "./gate";

export interface FirstRunResume {
  canvasId: string;
  brief: UserOnboardingFirstRunBrief;
  sessionId?: string;
}

export type FirstRunView =
  | { kind: "none" }
  | { kind: "question" }
  | { kind: "primer"; resume: FirstRunResume };

const NONE: FirstRunView = { kind: "none" };

export function decideFirstRun(
  enabled: boolean,
  record: UserOnboarding | undefined,
  stage: Stage,
): FirstRunView {
  if (!enabled || !record || stage === "unresolved") return NONE;
  if (record.template_prompt) return NONE;
  const run = record.first_run;
  if (run?.status === "landed") return NONE;
  if (run?.status === "sent" && run.canvas_id) {
    return {
      kind: "primer",
      resume: {
        canvasId: run.canvas_id,
        brief: run.brief ?? "custom",
        sessionId: run.session_id,
      },
    };
  }
  if (stage === "new") return { kind: "question" };
  return NONE;
}

// Once the layer has left, or given up, nothing brings it back this session:
// a landed write that failed must not put the question in front of someone
// who is already on their canvas.
let dismissed = false;
// True from the landing write until the layer has faded, so the slot keeps
// it mounted while the record already says landed.
let leaving = false;
// One `started` write per page load: a remount of the layer is not a second
// start, and the server treats a repeat as a no-op anyway.
let started = false;
// Bumped by a dev reset, so the slot remounts a layer that is already up
// (mid-primer) on the question.
let generation = 0;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function dismissFirstRun() {
  dismissed = true;
  leaving = false;
  notify();
}

export function setFirstRunLeaving(on: boolean) {
  leaving = on;
  notify();
}

/** The first `started` write of this page load claims it; later ones are no-ops. */
export function claimFirstRunStart(): boolean {
  if (started) return false;
  started = true;
  return true;
}

/**
 * The dev reset (`?tourReset=1`): every latch back to a fresh page load, so
 * the question shows again without a reload while the override is on.
 */
export function resetFirstRunLatches() {
  dismissed = false;
  leaving = false;
  started = false;
  generation += 1;
  notify();
}

export function resetFirstRunForTest() {
  resetFirstRunLatches();
  generation = 0;
}

/** Changes on a dev reset; the slot keys the layer by it. */
export function useFirstRunGeneration(): number {
  return useSyncExternalStore(subscribe, () => generation);
}

/** Why a record shows nothing, for the dev who turned the override on. */
export function ineligibleReason(record: UserOnboarding, stage: Stage): string {
  return `stage=${stage}, template_prompt=${record.template_prompt ? "set" : "none"}, first_run.status=${record.first_run?.status ?? "none"}`;
}

// Two slots read the decision; the hint is said once per reason per load.
let hinted: string | null = null;

export function useFirstRun(): FirstRunView {
  const enabled = useFirstRunEnabled();
  const { record, stage } = useOnboardingRecord();
  const gone = useSyncExternalStore(subscribe, () => dismissed);
  const view = gone ? NONE : decideFirstRun(enabled, record, stage);
  const reason =
    import.meta.env.DEV &&
    isFirstRunOverridden() &&
    !gone &&
    view.kind === "none" &&
    record &&
    stage !== "unresolved"
      ? ineligibleReason(record, stage)
      : null;
  useEffect(() => {
    if (!reason || hinted === reason) return;
    hinted = reason;
    console.info(
      `[first run] the override is on but this record is not eligible: ${reason}. Reset it with ?tourReset=1 on the home.`,
    );
  }, [reason]);
  return view;
}

/** The landing write is in and the layer is still on its way out. */
export function useFirstRunLeaving(): boolean {
  return useSyncExternalStore(subscribe, () => leaving);
}

/** The layer is up, or about to be: the home keeps its welcome dialog away. */
export function useFirstRunPresent(): boolean {
  const view = useFirstRun();
  const exiting = useFirstRunLeaving();
  return view.kind !== "none" || exiting;
}
